/**
 * Minimal Model Context Protocol client over Streamable HTTP for the deployed niobe-mcp-graph
 * (Softeria M365 MCP, SharePoint-only, BYOT).
 *
 * The member's Graph token (from graphAuth.ts) is sent as `Authorization: Bearer …` on every call;
 * the server validates it against Graph and acts AS the member (per-user ACL preserved).
 *
 * DEV/CORS: the browser posts to a SAME-ORIGIN path (default `/graph-mcp`) which the Vite proxy
 * forwards to the container app's `/mcp` — so no cross-origin request leaves the browser and no
 * CORS headers are needed on the MCP server. Override the path with window.__NIOBE_CONFIG__.graph
 * .mcpUrl or VITE_GRAPH_MCP_URL (in prod that must be a CORS-enabled absolute URL, or keep proxying).
 *
 * The server answers with an SSE frame ("event: message\ndata: {json}\n\n"); we pull the data line.
 * It is stateless (no Mcp-Session-Id), so a bare initialize → tools/call sequence is enough.
 */

function endpoint(): string {
  return window.__NIOBE_CONFIG__?.graph?.mcpUrl ?? import.meta.env.VITE_GRAPH_MCP_URL ?? '/graph-mcp';
}

interface JsonRpcResponse {
  result?: unknown;
  error?: { code: number; message: string };
}

/** Parse a Streamable-HTTP body: SSE `data:` line(s) if framed, else raw JSON. */
function parseBody(text: string): JsonRpcResponse {
  const dataLines = text
    .split('\n')
    .filter((l) => l.startsWith('data:'))
    .map((l) => l.slice(5).trim());
  const payload = dataLines.length > 0 ? dataLines.join('') : text.trim();
  return JSON.parse(payload) as JsonRpcResponse;
}

let id = 0;

async function rpc(token: string, method: string, params?: unknown): Promise<unknown> {
  const res = await fetch(endpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json, text/event-stream',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ jsonrpc: '2.0', id: ++id, method, params }),
  });
  if (res.status === 401) throw new Error('MCP 401 — the Graph token was rejected (expired or wrong audience).');
  if (!res.ok) throw new Error(`MCP HTTP ${res.status}`);
  const parsed = parseBody(await res.text());
  if (parsed.error) throw new Error(`MCP error ${parsed.error.code}: ${parsed.error.message}`);
  return parsed.result;
}

export interface McpTool {
  name: string;
  description?: string;
}

/** MCP handshake. Cheap and stateless here; call once before tools. */
export async function initialize(token: string): Promise<void> {
  await rpc(token, 'initialize', {
    protocolVersion: '2024-11-05',
    capabilities: {},
    clientInfo: { name: 'niobe-web', version: '0.1.0' },
  });
}

export async function listTools(token: string): Promise<McpTool[]> {
  const result = (await rpc(token, 'tools/list')) as { tools?: McpTool[] };
  return result.tools ?? [];
}

export interface ToolCallResult {
  /** The first text content block, already JSON-parsed when it is a Graph JSON payload. */
  data: unknown;
  isError: boolean;
  raw: string;
}

/**
 * Call a tool and unwrap its single text content block. Softeria returns the Graph JSON (or an
 * error object) as a JSON string inside content[0].text — we parse it when possible.
 */
export async function callTool(
  token: string,
  name: string,
  args: Record<string, unknown>,
): Promise<ToolCallResult> {
  const result = (await rpc(token, 'tools/call', { name, arguments: args })) as {
    content?: Array<{ type: string; text?: string }>;
    isError?: boolean;
  };
  const raw = result.content?.find((c) => c.type === 'text')?.text ?? '';
  let data: unknown = raw;
  try {
    data = JSON.parse(raw);
  } catch {
    // Not JSON — leave as the raw string.
  }
  return { data, isError: result.isError ?? false, raw };
}
